// Pure data-processing helpers ported 1:1 from src/pages/home/Home.tsx — kept out of
// the page component so it doesn't get any heavier than it already needs to be.

export interface SummaryFilters {
  startInterval?: number
  endInterval?: number
  [key: string]: any
}

// Slices a NYHA "xAxis.categories" + "series[].data" pair down to the requested
// compilation interval, then caps it to the most recent 12 points — same logic (and
// same order of operations) as legacy Home.tsx's processNyhaData.
export function processNyhaData(data: any, currentFilters: SummaryFilters): any {
  if (!data || !data.xAxis || !data.series) return data

  const {startInterval, endInterval} = currentFilters
  let processedData = {...data}

  if (startInterval !== undefined || endInterval !== undefined) {
    const start = startInterval ?? 0
    const end = endInterval ?? data.xAxis.categories.length - 1

    processedData = {
      ...processedData,
      xAxis: {
        ...processedData.xAxis,
        categories: processedData.xAxis.categories.slice(start, end + 1),
      },
      series: processedData.series.map((s: any) => ({
        ...s,
        data: s.data.slice(start, end + 1),
      })),
    }
  }

  if (processedData.xAxis.categories && processedData.xAxis.categories.length > 12) {
    const startIndex = processedData.xAxis.categories.length - 12
    processedData = {
      ...processedData,
      xAxis: {
        ...processedData.xAxis,
        categories: processedData.xAxis.categories.slice(startIndex),
      },
      series: processedData.series.map((s: any) => ({
        ...s,
        data: s.data.slice(startIndex),
      })),
    }
  }

  return processedData
}

// Groups the raw questionnaireScores payload (one entry per questionnaire, each with
// its own xAxis/series) into { "<title>-<index>": [{key, label, title, notes, xAxis,
// type, series}, ...] } — one item per series (compilation), ready to feed a Tabs +
// ChartCard pair. Same logic as legacy Home.tsx's processGraphData.
export function processGraphData(data: any[], currentFilters: SummaryFilters): Record<string, any[]> {
  if (!Array.isArray(data)) return {}

  const processed: Record<string, any[]> = {}
  const {startInterval, endInterval} = currentFilters

  data.forEach((questionnaire, index) => {
    const {title, notes, xAxis, series} = questionnaire
    if (!series || series.length === 0) return

    const groupKey = `${title}-${index}`
    processed[groupKey] = []

    let filteredXAxisCategories = xAxis.categories
    let filteredSeries = series

    if ((xAxis.type === 'single' || xAxis.type === 'interval') && (startInterval !== undefined || endInterval !== undefined)) {
      const start = startInterval ?? 0
      const end = endInterval ?? xAxis.categories.length
      filteredXAxisCategories = xAxis.categories.slice(start, end + 1)
      filteredSeries = series.map((s: any) => ({
        ...s,
        data: s.data.slice(start, end + 1),
      }))
    }

    filteredSeries.forEach((s: any) => {
      processed[groupKey].push({
        key: s.name,
        label: s.name,
        title,
        notes,
        xAxis: filteredXAxisCategories,
        type: xAxis.type,
        series: [{
          name: s.name,
          data: s.data,
          type: 'column',
        }],
      })
    })
  })

  return processed
}

// Max value for the "Intervallo di compilazioni" range filter: the longest xAxis
// across any single/interval-indexed questionnaire graph, or the NYHA aggregate,
// whichever is longer. Same fallback (20) as legacy Home.tsx.
export function calculateMaxCompilation(questionnaireData: any[], nyhaData: any): number {
  if (!questionnaireData && !nyhaData) return 20

  const maxFromQuestionnaires = questionnaireData
    ? Object.values(processGraphData(questionnaireData, {}))
      .flat()
      .filter((graph: any) => graph.type === 'single' || graph.type === 'interval')
      .reduce((acc: number, graph: any) => Math.max(acc, graph.xAxis?.length || 0), 0)
    : 0

  const maxFromNyha = nyhaData?.xAxis?.categories?.length || 0
  const max = Math.max(maxFromQuestionnaires, maxFromNyha)

  return max > 0 ? max - 1 : 20
}

// The "Intervallo di compilazioni" filter only slices compilation-indexed axes (T0..Tn):
// questionnaire graphs with xAxis type single/interval, and the NYHA aggregate when it
// actually has categories. Calendar-month ("date") questionnaire graphs ignore it.
export function hasCompilationIndexedGraphs(questionnaireGraphs: Record<string, any[]>, nyhaData: any, nyhaEnabled?: boolean): boolean {
  const hasTimeQuestionnaire = Object.values(questionnaireGraphs ?? {})
    .flat()
    .some((graph: any) => graph?.type === 'single' || graph?.type === 'interval')
  const hasNyha = Boolean(nyhaEnabled) && (nyhaData?.xAxis?.categories?.length ?? 0) > 0
  return hasTimeQuestionnaire || hasNyha
}
